import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MultiSelectOption } from '../../../components/ui/WizardMultiSelectChips';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import * as publicPetitionService from '../../../services/publicPetitionCaseRequests';

export function useCaseCategorization(
  fileCategoryId: string,
  subFileCategoryId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [subCategories, setSubCategories] = useState<LookupItem[]>([]);
  const [applicableAreas, setApplicableAreas] = useState<LookupItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(enabled);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoadingCategories(false);
      return;
    }

    let mounted = true;
    setLoadingCategories(true);

    void publicPetitionService.getFileCategories().then((r) => {
      if (!mounted) return;
      if (r.success) setFileCategories(r.data);
      setLoadingCategories(false);
    });

    return () => {
      mounted = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !fileCategoryId) {
      setSubCategories([]);
      return;
    }
    let mounted = true;
    setLoadingSubCategories(true);
    void publicPetitionService.getSubFileCategories(fileCategoryId).then((r) => {
      if (!mounted) return;
      if (r.success) setSubCategories(r.data);
      setLoadingSubCategories(false);
    });
    return () => {
      mounted = false;
    };
  }, [enabled, fileCategoryId]);

  useEffect(() => {
    if (!enabled || !subFileCategoryId) {
      setApplicableAreas([]);
      return;
    }
    let mounted = true;
    setLoadingAreas(true);
    void publicPetitionService.getSubCategoryApplicableAreas(subFileCategoryId).then((r) => {
      if (!mounted) return;
      if (r.success) setApplicableAreas(r.data);
      setLoadingAreas(false);
    });
    return () => {
      mounted = false;
    };
  }, [enabled, subFileCategoryId]);

  const applicableAreaOptions: MultiSelectOption[] = useMemo(
    () => applicableAreas.map((a) => ({ id: a.id, label: a.name })),
    [applicableAreas],
  );

  const resolveAreaLabels = useCallback(
    (ids: string[]) => ids.map((id) => applicableAreas.find((a) => a.id === id)?.name ?? id).filter(Boolean),
    [applicableAreas],
  );

  const fileCategoryName = useMemo(
    () => fileCategories.find((c) => c.id === fileCategoryId)?.name ?? '',
    [fileCategories, fileCategoryId],
  );

  const subFileCategoryName = useMemo(
    () => subCategories.find((s) => s.id === subFileCategoryId)?.name ?? '',
    [subCategories, subFileCategoryId],
  );

  return {
    fileCategories,
    subCategories,
    applicableAreaOptions,
    loadingCategories,
    loadingSubCategories,
    loadingAreas,
    fileCategoryName,
    subFileCategoryName,
    resolveAreaLabels,
    hasApplicableAreas: applicableAreas.length > 0,
  };
}
